import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { listingsAPI, brandsAPI, categoriesAPI } from '../utils/api';
import { toast } from 'sonner';

const CONDITIONS = ['New', 'Excellent', 'VeryGood'];

const WatchForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    brand: '', category: '', model: '', ref: '', year: new Date().getFullYear(), condition: 'New',
    caseSize: '', material: '', movement: '', dialColor: '', strap: '', boxPapers: '',
    price: '', description: '', image: '', images: [],
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    const loadData = async () => {
      const [b, localCats] = await Promise.all([
        brandsAPI.getAll(),
        categoriesAPI.getLocal().catch(() => []),
      ]);
      setBrands(b);
      // Category dropdown uses locally managed categories only
      setCategories(Array.isArray(localCats) ? localCats.map(c => ({ id: c.id, name: c.name })) : []);
    };
    loadData();
    if (isEdit) {
      listingsAPI.getById(id).then(w => {
        if (w) setForm({ brand: w.brand, category: w.category, model: w.model, ref: w.ref, year: w.year || '',
          condition: w.condition, caseSize: w.caseSize, material: w.material, movement: w.movement,
          dialColor: w.dialColor, strap: w.strap, boxPapers: w.boxPapers, price: w.price,
          description: w.description, image: w.image || w.images?.[0] || '', images: w.images || [] });
      });
    }
  }, [id, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.brand || !form.model || !form.price) { toast.error('Brand, Model, and Price are required'); return; }
    setSubmitting(true);
    try {
      const data = { ...form, images: form.image ? [form.image, ...(form.images || [])] : form.images };
      if (isEdit) { await listingsAPI.update(id, data); toast.success('Watch updated'); }
      else { await listingsAPI.create(data); toast.success('Watch added to catalog'); }
      navigate('/watches');
    } catch (e) { toast.error(e.message || 'Failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="watch-form-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{isEdit ? 'Edit Watch' : 'Add a Watch'}</h1>
        <p className="text-muted-foreground text-sm mt-1">Fill in the details to {isEdit ? 'update this' : 'list a new'} timepiece</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Section 1: Identity */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-sm">Identity</CardTitle><CardDescription>Core watch information</CardDescription></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Brand *</Label>
              <Select value={form.brand} onValueChange={v => set('brand', v)}>
                <SelectTrigger><SelectValue placeholder="Select brand" /></SelectTrigger>
                <SelectContent>{brands.map(b => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Category</Label>
              <Select value={form.category} onValueChange={v => set('category', v)}>
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Model *</Label>
              <Input placeholder="Submariner Date" value={form.model} onChange={e => set('model', e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Reference</Label>
              <Input placeholder="126610LN" value={form.ref} onChange={e => set('ref', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Year</Label>
              <Input type="number" placeholder="2024" value={form.year} onChange={e => set('year', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Condition</Label>
              <Select value={form.condition} onValueChange={v => set('condition', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CONDITIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Specifications */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-sm">Specifications</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-muted-foreground">Case Size</Label><Input placeholder="41mm" value={form.caseSize} onChange={e => set('caseSize', e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-muted-foreground">Material</Label><Input placeholder="Oystersteel" value={form.material} onChange={e => set('material', e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-muted-foreground">Movement</Label><Input placeholder="Calibre 3235" value={form.movement} onChange={e => set('movement', e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-muted-foreground">Dial Color</Label><Input placeholder="Black" value={form.dialColor} onChange={e => set('dialColor', e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-muted-foreground">Strap</Label><Input placeholder="Oyster bracelet" value={form.strap} onChange={e => set('strap', e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-muted-foreground">Box & Papers</Label><Input placeholder="Full Set" value={form.boxPapers} onChange={e => set('boxPapers', e.target.value)} /></div>
          </CardContent>
        </Card>

        {/* Section 3: Listing */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-sm">Listing</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Price (USD) *</Label>
              <Input type="number" placeholder="14100" value={form.price} onChange={e => set('price', e.target.value)} required min="1" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Image</Label>
              <div className="flex gap-2">
                <Input placeholder="https://... or upload below" value={form.image?.startsWith('data:') ? 'Uploaded image' : form.image} onChange={e => set('image', e.target.value)} className="flex-1" />
                <label className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-input bg-background text-xs cursor-pointer hover:bg-accent hover:text-accent-foreground transition-colors shrink-0">
                  Upload
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = () => {
                      const img = new Image();
                      img.onload = () => {
                        const canvas = document.createElement('canvas');
                        const MAX = 600;
                        let w = img.width, h = img.height;
                        if (w > MAX || h > MAX) { if (w > h) { h = Math.round(h * MAX / w); w = MAX; } else { w = Math.round(w * MAX / h); h = MAX; } }
                        canvas.width = w; canvas.height = h;
                        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
                        set('image', canvas.toDataURL('image/jpeg', 0.7));
                      };
                      img.src = reader.result;
                    };
                    reader.readAsDataURL(file);
                    e.target.value = '';
                  }} />
                </label>
              </div>
              {form.image && <img src={form.image} alt="Preview" className="w-full max-w-sm h-40 object-cover rounded-lg border border-border mt-1" />}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Description</Label>
              <Textarea placeholder="Describe the watch, its history, and condition details..." value={form.description} onChange={e => set('description', e.target.value)} rows={4} />
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-start gap-3">
          <Button type="submit" disabled={submitting} className="bg-primary text-primary-foreground hover:bg-[hsl(42,44%,62%)]">
            {submitting ? 'Saving...' : (isEdit ? 'Update Watch' : 'Save')}
          </Button>
          <Button type="button" variant="ghost" onClick={() => navigate('/watches')}>Cancel</Button>
        </div>
      </form>
    </div>
  );
};

export default WatchForm;
